import { ActionButton } from '../parts/index.js';
// The monitoring policy's words, drawn with their structure (module 10 U129): a title, the
// sections with a way to jump to each, lists as lists, a bold lead-in where the author put
// one. One component, used by the staff page and by the admin's reading of a draft, so what
// an admin reads before publishing is what staff are shown afterwards.
//
// The words are the company's and are shown whole (R22). policy-words.ts reads them into
// blocks and loses nothing; this draws the blocks as React text, never as HTML, so nothing
// typed into a policy can become markup.
import { Fragment, useId, useMemo } from 'react';
import { policyBlocks, policySections, type Run } from './policy-words.js';

const Runs = ({ runs }: { runs: Run[] }) => (
  <>{runs.map((r, i) => (r.bold ? <strong key={i}>{r.text}</strong> : <Fragment key={i}>{r.text}</Fragment>))}</>
);

/** Whether the person has asked their machine for less motion. */
export const calm = (): boolean => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/** A contents list earns its place once there is something to jump between. */
const CONTENTS_FROM = 3;

export function PolicyWords({ body }: { body: string }) {
  const blocks = useMemo(() => policyBlocks(body), [body]);
  const sections = useMemo(() => policySections(blocks), [blocks]);
  const uid = useId();
  const anchor = (n: number) => `${uid}-s${n}`;
  // The heading takes the focus as well as the scroll, so a keyboard carries on from where it
  // landed; and a person who has asked for less motion is taken there at once.
  const jump = (n: number) => {
    const h = document.getElementById(anchor(n));
    if (!h) return;
    h.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth', block: 'start' });
    h.focus({ preventScroll: true });
  };

  let section = -1;
  return (
    <div className="policy-words">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'title': return <h1 key={i}><Runs runs={b.runs} /></h1>;
          case 'section': {
            section += 1;
            const first = section === 0 && sections.length >= CONTENTS_FROM;
            const n = section;
            return (
              <Fragment key={i}>
                {first ? (
                  <nav className="policy-contents" aria-label="In this policy">
                    <p>In this policy</p>
                    <ol>
                      {sections.map((s, k) => <li key={k}><ActionButton reading type="button" onClick={() => jump(k)}>{s}</ActionButton></li>)}
                    </ol>
                  </nav>
                ) : null}
                <h2 id={anchor(n)} tabIndex={-1}><Runs runs={b.runs} /></h2>
              </Fragment>
            );
          }
          case 'part': return <h3 key={i}><Runs runs={b.runs} /></h3>;
          case 'para': return <p key={i}><Runs runs={b.runs} /></p>;
          case 'list': {
            const items = b.items.map((it, k) => <li key={k}><Runs runs={it} /></li>);
            return b.ordered ? <ol key={i} start={b.start}>{items}</ol> : <ul key={i}>{items}</ul>;
          }
        }
      })}
    </div>
  );
}
